import {useQueryClient, type Query, type QueryClient} from '@github-ui/react-query'
import {useCallback} from 'react'

import {milestoneEditQueryKey} from './milestone-edit-route'
import {isRepositoryMilestoneIndexQuery} from './milestone-index-route'
import {milestoneHeaderQueryKey} from './milestone-show-route'

/**
 * Refreshes every cached index variant for this repository and, by default, one Show header.
 * Omitting the milestone number performs index-only synchronization.
 * Cancel before invalidating or removing so pre-write reads cannot restore stale data.
 *
 * By default the affected milestone's Edit route query is removed rather than invalidated:
 * that route is a Blocking query, so removal forces the next Edit visit — including browser Back
 * to a previously warm cache — to block on a fresh fetch instead of initializing `MilestoneForm`'s
 * one-time `useState` from a stale payload that a background revalidation would not repair.
 * Index close/delete also evict inactive variants so changing tabs cannot render their old data.
 * Native optimistic Edit skips removal while it settles the original query, including browser Back
 * during the write, then evicts the query only if it is inactive.
 */
export async function syncMilestoneCaches(
  queryClient: QueryClient,
  owner: string,
  repo: string,
  milestoneNumber?: number,
  {
    header = 'invalidate',
    edit = 'remove',
    evictInactiveIndex = false,
  }: {
    header?: 'invalidate' | 'remove' | 'skip'
    edit?: 'remove' | 'skip'
    evictInactiveIndex?: boolean
  } = {},
) {
  const pathname = `/${owner}/${repo}/milestones`
  const indexPredicate = (query: Query) => isRepositoryMilestoneIndexQuery(query, pathname)
  const headerQueryKey =
    milestoneNumber === undefined || header === 'skip'
      ? undefined
      : milestoneHeaderQueryKey(owner, repo, milestoneNumber)
  const editQueryKey =
    milestoneNumber === undefined || edit === 'skip' ? undefined : milestoneEditQueryKey(owner, repo, milestoneNumber)

  await Promise.all([
    queryClient.cancelQueries({predicate: indexPredicate}),
    headerQueryKey ? queryClient.cancelQueries({queryKey: headerQueryKey, exact: true}) : undefined,
    editQueryKey ? queryClient.cancelQueries({queryKey: editQueryKey, exact: true}) : undefined,
  ])
  if (evictInactiveIndex) {
    queryClient.removeQueries({predicate: indexPredicate, type: 'inactive'})
  }
  if (headerQueryKey && header === 'remove') {
    queryClient.removeQueries({queryKey: headerQueryKey, exact: true})
  }
  if (editQueryKey) {
    queryClient.removeQueries({queryKey: editQueryKey, exact: true})
  }
  await Promise.all([
    queryClient.invalidateQueries({predicate: indexPredicate}),
    headerQueryKey && header === 'invalidate'
      ? queryClient.invalidateQueries({queryKey: headerQueryKey, exact: true})
      : undefined,
  ])
}

export function useMilestoneCacheSync(owner: string, repo: string) {
  const queryClient = useQueryClient()

  return useCallback(
    (milestoneNumber?: number, options?: Parameters<typeof syncMilestoneCaches>[4]) =>
      syncMilestoneCaches(queryClient, owner, repo, milestoneNumber, options),
    [owner, queryClient, repo],
  )
}
